import { Button } from '../ui/Button';
import { useTranslation } from '../../../lib/i18n';
import { logPageClickBi } from '../../../lib/bi-logger';
import { CHAT_HOME_BI } from './message-utils';
import { connectorLogoUrl } from '../../../lib/connectors';
import { connectHrefFor } from '../../../lib/suggested-task-connect';
import { useSuggestedTasks, type SuggestedTask } from '../../../lib/suggested-tasks';

/**
 * The suggested-task chips under an empty composer — the genome's answer to "what could I ask for?",
 * personalized per business and refreshed on every genome recalc.
 *
 * <p>Three rules the design turns on:
 *
 * 1. **A tap on a READY chip PREFILLS, it does not send** — and hovering first SHOWS what would be
 *    sent. These are the assistant's guesses, and a guess that acts on its own is one the user cannot
 *    decline. The reveal is the preview; the composer keeps the last word.
 * 2. **The row is silent when it has nothing.** Loading, failed, or none authored all render null.
 *    The composer below is the feature; a spinner or an error where six optional buttons would be is
 *    strictly worse than the buttons never appearing.
 * 3. **A connect chip says so on its face, and a tap on it goes and fixes that.** At most one chip
 *    names an app the business has not connected; it carries a visible marker, not just a tooltip,
 *    AND its tap lands on that connector's card on the Connectors page (`connectHrefFor`) instead of
 *    prefilling the composer — a prefilled prompt for an unconnected app would only bounce the ask
 *    back at the user one turn later, in conversation, which is the dead end the marker exists to warn
 *    about in the first place. See `lib/suggested-task-connect.ts`.
 *
 * **Written-down parity exception — the reveal is hover-only, so touch web does not get it.** iOS and
 * Android long-press for the same text; on the web the reveal is the native `title` tooltip, which has
 * no touch equivalent. A mobile-web user can still read the prompt — it lands in the composer, above
 * the keyboard, before they send — so the information is not lost, only its preview. Fixing it properly
 * means a bespoke press-and-hold popover, which is more machinery than the gap justifies today; revisit
 * if mobile web becomes a primary chat surface. (Repo golden rule: an exception is written down, never
 * a silent gap.)
 */
export function SuggestedTaskChips({
  enabled,
  narrow,
  onPick,
}: {
  /** Only fetch when the empty landing is actually showing — no chips, no request. */
  enabled: boolean;
  narrow: boolean;
  /**
   * The chip was tapped. The caller puts `task.prompt` in the composer and focuses it; it must NOT send.
   *
   * The whole task travels, not just its prompt: the caller also parks `task.label` so that IF the user
   * goes on to send, the chip can be recorded as acted on and stop coming back. The label is what
   * identifies the chip to the server — see `lib/suggested-task-ack.ts`. Same shape iOS and Android
   * settled on for the same reason.
   */
  onPick: (task: SuggestedTask) => void;
}) {
  const { t } = useTranslation();
  const state = useSuggestedTasks(enabled);
  if (state.status !== 'ready') return null;

  return (
    <div style={{ ...WRAP, padding: narrow ? '0 16px 20px' : '0 48px 24px' }}>
      {state.tasks.map((task, i) => (
        <Chip key={`${task.label}-${i}`} task={task} connectLabel={t('common.connect')} narrow={narrow} onPick={onPick} />
      ))}
    </div>
  );
}

function hideLogoOnError(e: React.SyntheticEvent<HTMLImageElement>) {
  // A logo CDN that 404s a slug must cost the chip its picture, not its text.
  (e.currentTarget as HTMLImageElement).style.display = 'none';
}

/**
 * The chip's `<Button>`, in whichever of the two shapes ButtonProps actually offers — a link when
 * `connectHref` is set, a plain button otherwise. Split out as its own component (rather than a shared
 * props object spread into one `<Button>` call) because that object's type is a union of the two
 * shapes, and TS cannot narrow a *spread* of a union the way it narrows a value — see
 * https://github.com/microsoft/TypeScript/issues/28486. `commonProps` below is NOT itself a union
 * (both call sites build it from the same literal shape), so each of the two returns individually type-checks
 * clean against the one ButtonProps branch it uses.
 */
function ChipButton({
  connectHref,
  title,
  ariaLabel,
  onClick,
  style,
  startIcon,
  endIcon,
  children,
}: {
  connectHref: string | null;
  title: string;
  ariaLabel: string;
  onClick: () => void;
  style: React.CSSProperties;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children: React.ReactNode;
}) {
  const commonProps = { variant: 'outlined' as const, size: 'small' as const, title, 'aria-label': ariaLabel, style, startIcon, endIcon };
  if (connectHref) {
    return (
      <Button {...commonProps} href={connectHref} onClick={onClick}>
        {children}
      </Button>
    );
  }
  return (
    <Button {...commonProps} onClick={onClick}>
      {children}
    </Button>
  );
}

function Chip({
  task,
  connectLabel,
  narrow,
  onPick,
}: {
  task: SuggestedTask;
  connectLabel: string;
  /**
   * The chip lives in a tight pane (docked side panel or a narrow viewport). Only then does the
   * label wrap: a suggestion you can't read is one you can't pick. When the chat is EXPANDED there is
   * room for the original single-line pill, so it keeps that look — no cap, no wrap, no truncation.
   */
  narrow: boolean;
  onPick: (task: SuggestedTask) => void;
}) {
  // The reveal shows the PROMPT — the exact text a tap puts in the composer — so the user can read
  // what they are about to send before they send it. (`why` is the internal rationale; it rides the
  // accessibility label below, where length is free, rather than competing for the tooltip.) A connect
  // chip is prefixed with its state, the more urgent thing to know before tapping.
  const tooltip = task.requiresConnect ? `${connectLabel} · ${task.prompt}` : task.prompt;
  // A `requiresConnect` chip's own marker already tells the reader it isn't ready to run, so tapping it
  // goes straight to connecting the app instead of prefilling a prompt that would only bounce back
  // asking for the same connection one turn later. `connectHref` is null when the task names no
  // connector to land on — that chip falls back to the ordinary prefill, same as a chip that was never
  // marked `requiresConnect` at all.
  const connectHref = task.requiresConnect ? connectHrefFor(task.connector) : null;
  const ariaLabel = task.why ? `${task.label} — ${task.why}` : task.label;
  const onClick = connectHref
    ? () => {
        logPageClickBi({ ...CHAT_HOME_BI, button_name: 'suggested_task_connect', location: 'composer' });
      }
    : () => {
        // Which chip was picked, not merely that one was — parity with iOS/Android, which both log this.
        // Without it, any read on which suggestions earn their place is blind to desktop.
        logPageClickBi({ ...CHAT_HOME_BI, button_name: 'suggested_task_selected', location: 'composer' });
        onPick(task);
      };

  // Expanded chat: the original single-line pill — logo + label + trailing Connect marker, no cap.
  if (!narrow) {
    return (
      <ChipButton
        connectHref={connectHref}
        title={tooltip}
        ariaLabel={ariaLabel}
        onClick={onClick}
        style={{ maxWidth: '100%', opacity: task.requiresConnect ? 0.72 : 1 }}
        startIcon={
          task.connector ? (
            <img src={connectorLogoUrl(task.connector)} alt="" aria-hidden width={14} height={14} style={WIDE_ICON} onError={hideLogoOnError} />
          ) : undefined
        }
        endIcon={task.requiresConnect ? <span style={CONNECT_MARK}>{connectLabel}</span> : undefined}
      >
        <span style={LABEL}>{task.label}</span>
      </ChipButton>
    );
  }

  // Tight pane: a multi-line pill. Label WRAPS instead of truncating, and the logo, label and Connect
  // marker are one INLINE flow (not a flex row) so the marker stays beside a short label and drops to
  // its own line only once the text fills the row — no measurement needed.
  return (
    <ChipButton connectHref={connectHref} title={tooltip} ariaLabel={ariaLabel} onClick={onClick} style={{ ...CHIP, opacity: task.requiresConnect ? 0.72 : 1 }}>
      {task.connector && (
        <img src={connectorLogoUrl(task.connector)} alt="" aria-hidden width={14} height={14} style={ICON} onError={hideLogoOnError} />
      )}
      {task.label}
      {task.requiresConnect && (
        <>
          {' '}
          <span style={CONNECT_MARK}>{connectLabel}</span>
        </>
      )}
    </ChipButton>
  );
}

const WRAP: React.CSSProperties = {
  width: '100%',
  maxWidth: 760,
  alignSelf: 'center',
  boxSizing: 'border-box',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  // Chips are now multi-line, so pin them to the top of each row rather than letting the flex default
  // (stretch) grow a short pill to match a taller wrapped neighbour.
  alignItems: 'flex-start',
  gap: 8,
};

// The DS Button is a single-line, nowrap, centred control by default; a suggested-task chip is a
// multi-line pill, so these overrides turn it into one. `overflowWrap: anywhere` inherits into the
// label so a single very long word can't overflow the cap. The 320px cap is what makes wrapping
// visible — uncapped, a long label just stretches toward the row's full width and never wraps.
const CHIP: React.CSSProperties = {
  maxWidth: 'min(100%, 320px)',
  height: 'auto',
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  textAlign: 'left',
  justifyContent: 'flex-start',
  padding: 'var(--ds-space-8) var(--ds-space-12)',
  lineHeight: '18px',
};

const ICON: React.CSSProperties = {
  width: 14,
  height: 14,
  borderRadius: 3,
  // Inline so it rides the first text line, not a flex item vertically centred against the whole block.
  verticalAlign: 'middle',
  position: 'relative',
  top: -1,
  marginRight: 6,
};

// The expanded (single-line) pill: the DS Button's own flex row carries the logo as a startIcon, so
// it stays a flex item that never shrinks, and the label truncates rather than wrapping.
const WIDE_ICON: React.CSSProperties = {
  width: 14,
  height: 14,
  borderRadius: 3,
  flexShrink: 0,
};

const LABEL: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const CONNECT_MARK: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  // Never shrink or break the marker itself; only the space before it (narrow) is a break opportunity.
  flexShrink: 0,
  whiteSpace: 'nowrap',
  fontSize: 11,
  color: 'rgba(var(--ink-rgb), 0.45)',
  border: '1px solid rgba(var(--hairline-rgb), 0.5)',
  borderRadius: 999,
  padding: '1px 6px',
};
